import * as React from "react";
import styled from "@emotion/styled";
import { Button, FlexRow, IconArrow } from "atoms";
import { ProductWizardTemplate } from "./ProductWizardTemplate";
import { StepButton } from "./WizardStepOption";
import {
  comingSoonTemplates,
  productTemplateSteps,
  templateCategories,
} from "../data/productTemplates";
import type {
  ProductTemplate,
  ProductTemplateSteps,
  TemplateCategory,
} from "../types";

const TITLE = "Create New Product";

const templatesByCategory = productTemplateSteps.reduce<
  Record<TemplateCategory, ProductTemplateSteps[]>
>(
  (acc, cur) => {
    acc[cur.templateCategory].push(cur);
    return acc;
  },
  {
    "Fine Art Print": [],
    "Spiral Notebook": [],
    "Digital Download": [],
    "Onchain Collectible": [],
  },
);

type ChooseProductTemplateProps = {
  artistId: string;
  templates: ProductTemplate[];
};

function ChooseProductTemplate({
  artistId,
  templates,
}: ChooseProductTemplateProps) {
  const [state, setState] = React.useState<{
    category?: TemplateCategory;
    template?: ProductTemplateSteps;
  }>({});

  // First, choose category of templates
  if (!state.category) {
    const createOnClick = (category: TemplateCategory) => () => {
      // If the category only has one associated template, select it as well to skip step 2
      if (templatesByCategory[category].length === 1) {
        setState({ category, template: templatesByCategory[category][0] });
      } else {
        setState({ category });
      }
    };

    return (
      <ProductWizardTemplate
        title={TITLE}
        subtitle="What type of product would you like to sell?"
        artistId={artistId}
      >
        <SGrid>
          {templateCategories.map((category) => (
            <StepButton
              key={category.name}
              label={category.title ?? category.name}
              image={category.image}
              description={category.description}
              onClick={createOnClick(category.name)}
            />
          ))}
          {comingSoonTemplates.map((category) => (
            <StepButton
              key={category.name}
              label={category.name}
              image={category.image}
              description={category.description}
              isDisabled
              // type="disabled"
              // isComingSoon
            />
          ))}
        </SGrid>
      </ProductWizardTemplate>
    );
  }

  // Second, choose the template (unless the above useEffect selected it automatically)
  if (!state.template) {
    const moveBack = () => setState({});
    const createOnClick = (template: ProductTemplateSteps) => () =>
      setState({ ...state, template });

    return (
      <ProductWizardTemplate
        title={TITLE}
        subtitle={`Choose a ${state.category} product`}
        artistId={artistId}
      >
        <FlexRow justifyContent="center" style={{ marginBlock: 18 }}>
          {/* TODO: Create better "back" button function */}
          <Button size="xxs" variant="text" onClick={moveBack}>
            <small>
              <IconArrow dir="l" />
            </small>{" "}
            Choose a Different Category
          </Button>
        </FlexRow>
        <SGrid>
          {templatesByCategory[state.category].map((template) => (
            <StepButton
              key={template.id}
              onClick={createOnClick(template)}
              label={template.name}
              image="placeholder"
            />
          ))}
        </SGrid>
      </ProductWizardTemplate>
    );
  }

  // Lastly, show the CreateStep component for this template
  const {
    template: { CreateStep, id },
    category,
  } = state;

  // Find the template data from backend for this template id
  const productTemplate = templates.find((t) => t.id === id);
  if (!productTemplate) {
    throw new Error(`There is no template data for id: ${id}`);
  }

  // Create a back action for the step component, restarting at the beginning if the template was
  // automatically selected as the only template of its category, or at step 2 to repick template
  const moveBack =
    templatesByCategory[category].length === 1
      ? () => setState({})
      : () => setState({ category });

  return (
    <CreateStep
      artistId={artistId}
      productTemplate={productTemplate}
      moveBack={moveBack}
    />
  );
}

const SGrid = styled.div(({ theme }) => ({
  display: "grid",
  maxWidth: 800,
  marginInline: "auto",
  gap: theme.spacing.gap,
  gridTemplateColumns: "repeat(2, 1fr)",
  // "@media (min-width: 700px)": {
  [theme.breakpoints.tablet]: {
    gridTemplateColumns: "repeat(3, 1fr)",
  },
}));

export { ChooseProductTemplate };
